// Where field staff land (PRD 89 §4.12, §4.14) — core, because the two field
// roles and their gate are core. One answer for the "/" landing, the
// /business index, a business switch, the "not part of your role" notice and
// the invitation accept page.
//
// PRESENTATION ONLY. The api's RestrictedRoleGate is the security: a page a
// role cannot use answers 403 role_forbidden whatever this says. This keeps
// a technician from being greeted by a refusal (the dashboard) or a paywall.

import { BUSINESS_PARAM } from '../../utils/clientRequestLinks';

/** My profile: a technician's own page. */
export const MY_PROFILE_PATH = '/business/field/me';
/** My day: a technician's landing since PRD 95 (their visits and their clock). */
export const MY_DAY_PATH = '/business/field/my-day';
/** PRD 95's other technician pages: their week, and a visit's screen. */
const TECHNICIAN_DAY_PAGES = ['/business/field/my-time', '/business/field/visits'];
/** The dispatcher's landing: the field service Overview, until PRD 91's board. */
export const DISPATCHER_HOME_PATH = '/business/field';
/** PRD 118 §5.8: where a store's register sign-in lands, and the only page it opens. */
export const POS_REGISTER_PATH = '/business/pos/register';
/**
 * PRD 121 §4.10: the front desk lands on the appointment book (PRD 122's
 * calendar; until it ships that route redirects to the Overview). A provider
 * lands on their own page, `me` (Wave 2), until PRD 122's day.
 */
export const FRONT_DESK_HOME_PATH = '/business/appointments';
export const PROVIDER_HOME_PATH = '/business/appointments/me';
/** The personal services Overview, which every member may open. */
export const APPOINTMENTS_OVERVIEW_HOME_PATH = '/business/appointments/overview';
/** The service menu: a provider reads the services they perform (PRD 121 Wave 3). */
export const APPOINTMENTS_MENU_HOME_PATH = '/business/appointments/menu';

export const FIELD_STAFF_ROLES = ['technician', 'dispatcher'] as const;

/**
 * The roles that work the business without its books: field staff, the
 * register sign-in (PRD 118), and personal services' front desk and provider
 * (PRD 121). They land on their own page and skip the personal paywall on the
 * business side (paywallScope).
 */
export const RESTRICTED_STAFF_ROLES = [...FIELD_STAFF_ROLES, 'register', 'front_desk', 'provider'] as const;

const isFieldRole = (role: string | null | undefined): boolean =>
  role != null && (RESTRICTED_STAFF_ROLES as readonly string[]).includes(role);

/** The role's own page, or null for a role that lands where everyone does. */
export function fieldRoleHomePath(role: string | null | undefined): string | null {
  if (role === 'technician') return MY_DAY_PATH;
  if (role === 'dispatcher') return DISPATCHER_HOME_PATH;
  if (role === 'register') return POS_REGISTER_PATH;
  if (role === 'front_desk') return FRONT_DESK_HOME_PATH;
  if (role === 'provider') return PROVIDER_HOME_PATH;
  return null;
}

/** The role's page in a named business — the accept page's way in. */
export function fieldRoleHomeIn(role: string | null | undefined, businessId: string): string | null {
  const home = fieldRoleHomePath(role);
  return home ? `${home}?${BUSINESS_PARAM}=${encodeURIComponent(businessId)}` : null;
}

/**
 * §4.14: true when any business carries a field role. Such a user meets no
 * Pro paywall (and no waitlist) on the business screens. Keyed on the ROLE,
 * not on `modules.field_service`: a lapsed business's technicians keep
 * read-only access to their work (PRD 42 Rule 4), and a field role can only be
 * granted while field service is set up and entitled.
 */
export function isFieldStaff(businesses: ReadonlyArray<{ role?: string | null }>): boolean {
  return businesses.some(business => isFieldRole(business.role));
}

/**
 * §4.12's landing rung: a user whose EVERY membership is a field role goes to
 * the business side, where the /business index sends each business's role to
 * its own page. Null for anyone else (no businesses, or any books role).
 */
export function fieldStaffLanding(businesses: ReadonlyArray<{ role?: string | null }>): string | null {
  if (businesses.length === 0) return null;
  if (!businesses.every(business => isFieldRole(business.role))) return null;
  return '/business';
}

const matchesPrefix = (pathname: string, prefix: string): boolean =>
  pathname === prefix || pathname.startsWith(`${prefix}/`);

// The pages under /business each field role can use: its nav keys' routes
// (Experiences::Catalog::ROLE_EXPERIENCES) and nothing behind them. Settings
// stays reachable for the pending-invitation inbox and leaving a business.
const ROLE_PAGES: Record<(typeof RESTRICTED_STAFF_ROLES)[number], string[]> = {
  technician: [MY_DAY_PATH, MY_PROFILE_PATH, ...TECHNICIAN_DAY_PAGES],
  dispatcher: ['/business/field', '/business/customers', '/business/service-items'],
  register: [POS_REGISTER_PATH],
  // PRD 121: the module's pages. The front desk uses all of Appointments
  // (Staff, Time off, Locations, the Menu, me; the calendar later); a provider
  // their own page, the Overview and the menu (their own services, read —
  // PLAN-W3 api slice 3). Nothing lives at /business/staff (PLAN-W2 ruling 4).
  front_desk: ['/business/appointments'],
  provider: [PROVIDER_HOME_PATH, APPOINTMENTS_OVERVIEW_HOME_PATH, APPOINTMENTS_MENU_HOME_PATH],
};

/**
 * Whether a field role has any use for this route. False sends the shell's
 * business switch to the role's home instead of a refusal; any other role is
 * always true here (the gate and the page decide).
 */
export function fieldRoleAllows(role: string | null | undefined, pathname: string): boolean {
  if (!isFieldRole(role)) return true;
  if (matchesPrefix(pathname, '/business/settings')) return true;
  return ROLE_PAGES[role as (typeof RESTRICTED_STAFF_ROLES)[number]].some(prefix => matchesPrefix(pathname, prefix));
}
